import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ReferralMetadata } from '../models/referral-metadata';
import { ReferralMetadataService } from './referral-metadata.service';

export interface CementoCotizacionComunaRetiroDto {
  id?: number | null;
  code: string;
  name: string;
}

export interface CementoCotizacionAddressDto {
  lat: number;
  lng: number;
  comuna: string;
  region: string;
  regionCode: number | string;
  street: string;
  number: string;
  address: string;
}

export interface CementoCotizacionBillingDto {
  email: string;
  name: string;
  rut: string;
  giro: string;
  address: string;
  phone: string;
}

export interface CementoCotizacionContactDto {
  name: string;
  phone: string;
  delivery_instructions: string;
}

export interface CementoCotizacionSavePayload {
  order_buy?: string;
  product_id: number;
  incoterm: string;
  tipo_entrega: string;
  tipo_despacho?: number;
  comuna_retiro?: CementoCotizacionComunaRetiroDto;
  quantity: number;
  quantity_pallets?: number;
  unit_price: number;
  total_price: number;
  lead_hours?: number;
  schedule_date: string;
  billing: CementoCotizacionBillingDto;
  contact?: CementoCotizacionContactDto;
  dispatch_address: CementoCotizacionAddressDto;
  access_point: CementoCotizacionAddressDto;
  same_dispatch_address?: boolean;
  accept_terms?: boolean;
  token: string;
  auth_user_id: number | null;
  resume_token?: string;
  referral_metadata?: ReferralMetadata;
}

export interface CementoCotizacionSaveResponse {
  id: number;
  order_buy: string;
}

export interface CementoCotizacionRecoverResponse {
  order_buy: string;
  expires_at?: string | null;
  is_valid?: boolean;
  producto: unknown;
  tipoEntrega: unknown;
  cantidadServicios: unknown;
  fechaServicios: unknown;
  facturacion: unknown;
  mapa: unknown;
}

export interface CementoCotizacionConfigResponse {
  cutoffHour: number;
  minScheduleDate?: string;
}

@Injectable({ providedIn: 'root' })
export class CementoCotizacionService {
  private readonly baseUrl = `${environment.apiUrl}cemento/cotizacion/`;

  constructor(
    private readonly http: HttpClient,
    private readonly referralMetadataService: ReferralMetadataService,
  ) {}

  saveQuote(payload: CementoCotizacionSavePayload): Observable<CementoCotizacionSaveResponse> {
    return this.http.post<CementoCotizacionSaveResponse>(`${this.baseUrl}save/`, this.withReferralMetadata(payload));
  }

  recoverQuote(orderBuy: string): Observable<CementoCotizacionRecoverResponse> {
    return this.http.get<CementoCotizacionRecoverResponse>(
      `${this.baseUrl}${encodeURIComponent(orderBuy)}/recover/`
    );
  }

  getConfig(leadHours?: number): Observable<CementoCotizacionConfigResponse> {
    if (typeof leadHours === 'number' && leadHours >= 0) {
      return this.http.get<CementoCotizacionConfigResponse>(
        `${this.baseUrl}config/?lead_hours=${encodeURIComponent(String(leadHours))}`
      );
    }
    return this.http.get<CementoCotizacionConfigResponse>(`${this.baseUrl}config/`);
  }

  private withReferralMetadata<T extends object>(payload: T): T {
    const metadata = this.referralMetadataService.getMetadata();
    if (!metadata) {
      return payload;
    }

    return {
      ...(payload as Record<string, unknown>),
      referral_metadata: metadata,
    } as T;
  }
}
